import { useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Info } from 'lucide-react';
import { toast } from 'sonner';
import { businessReportsApi, type CashFlowBreakdownResponse } from '@/api/businessReports';
import { Button } from '@/components/ui/button';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  cfBreakdownCategoryHints,
  formatCfBreakdownCategoryLabel,
  isCfBreakdownLineId,
} from '@/lib/cashFlowBreakdownLines';
import { cn } from '@/lib/utils';
import { CentralExpenseReportInclusionCheckbox } from '@/components/business-reports/CentralExpenseReportInclusionCheckbox';

type CashFlowBreakdownHoverProps = {
  lineId: string;
  label: string;
  consolidated: boolean;
  periodMode: 'MTD' | 'YTD';
  periodKey: string;
  fyStartYear?: number;
  hospitalId?: string;
  className?: string;
  children?: ReactNode;
};

export async function downloadCashFlowBreakdownCsv(opts: {
  lineId: string;
  periodMode: 'MTD' | 'YTD';
  periodKey: string;
  fyStartYear?: number;
  hospitalId?: string;
}) {
  const resp = await businessReportsApi.cashFlowBreakdownExport({
    lineId: opts.lineId,
    periodMode: opts.periodMode,
    month: opts.periodMode === 'MTD' ? opts.periodKey : undefined,
    fy: opts.periodMode === 'YTD' ? opts.fyStartYear : undefined,
    hospitalId: opts.hospitalId,
  });
  const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const periodSlug =
    opts.periodMode === 'MTD' ? opts.periodKey : `FY${opts.fyStartYear ?? 'YTD'}`;
  a.download = `cash-flow-${opts.lineId}-${periodSlug}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function BreakdownPanel({
  data,
  hints,
  loading,
  hintsOnly,
  onDownload,
  downloading,
  canDownload,
}: {
  data?: CashFlowBreakdownResponse;
  hints: ReturnType<typeof cfBreakdownCategoryHints>;
  loading: boolean;
  hintsOnly: boolean;
  onDownload: () => void;
  downloading: boolean;
  canDownload: boolean;
}) {
  if (!hintsOnly && loading) {
    return <p className="text-xs text-muted-foreground">Loading breakdown…</p>;
  }

  return (
    <div className="space-y-2 max-w-md">
      {hintsOnly ? (
        <p className="text-[11px] text-muted-foreground leading-snug">
          Hover a month value for cash movements in that month, or use MTD/YTD for the full list.
          Click a value to download the calculation as CSV.
        </p>
      ) : null}
      {data?.sourceNote || hints?.note ? (
        <p className="text-[11px] text-muted-foreground leading-snug">
          {data?.sourceNote || hints?.note}
        </p>
      ) : null}
      {hints && hints.categories.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">Sources / categories</p>
          <ul className="mt-1 text-[11px] text-slate-600 list-disc pl-4 space-y-0.5">
            {hints.categories.map((c) => (
              <li key={c}>{formatCfBreakdownCategoryLabel(c)}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {!hintsOnly && data && data.categorySummary.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">
            In period ({data.periodLabel})
          </p>
          <ul className="mt-1 text-[11px] space-y-0.5">
            {data.categorySummary.map((c) => (
              <li key={c.category} className="flex justify-between gap-3 tabular-nums">
                <span className="text-slate-600 truncate">{c.label}</span>
                <span className="font-medium shrink-0">
                  ₹{c.amount.toLocaleString('en-IN')}
                  <span className="text-muted-foreground font-normal"> · {c.count}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {!hintsOnly && data && data.items.length > 0 ? (
        <div>
          <p className="text-[11px] font-medium text-slate-700">
            Line items ({data.items.length}
            {data.items.length >= 400 ? '+' : ''})
          </p>
          <ul className="mt-1 max-h-52 overflow-y-auto text-[11px] divide-y divide-slate-100">
            {data.items.map((item) => {
              const isCentral = item.source === 'CENTRAL';
              const included = !isCentral || item.includedInReports !== false;
              return (
                <li
                  key={item.id}
                  className={cn(
                    'py-1.5 flex justify-between gap-2 items-start',
                    !included && 'opacity-60'
                  )}
                >
                  <div className="flex gap-2 min-w-0 flex-1">
                    {isCentral ? (
                      <CentralExpenseReportInclusionCheckbox
                        expenseId={item.id}
                        includedInReports={included}
                      />
                    ) : null}
                    <div className="min-w-0">
                      <p
                        className={cn(
                          'font-medium truncate',
                          included ? 'text-slate-800' : 'text-slate-500 line-through'
                        )}
                      >
                        {item.description ||
                          item.vendorName ||
                          item.expenseEmployeeName ||
                          item.categoryLabel}
                      </p>
                      <p className="text-muted-foreground truncate">
                        {item.categoryLabel}
                        {item.source ? ` · ${item.source}` : ''}
                        {item.vendorName ? ` · ${item.vendorName}` : ''}
                        {!included ? ' · Excluded from reports' : ''}
                      </p>
                    </div>
                  </div>
                  <span className="tabular-nums font-medium shrink-0">
                    ₹{item.amount.toLocaleString('en-IN')}
                  </span>
                </li>
              );
            })}
          </ul>
          {data.total > 0 ? (
            <p className="text-[11px] font-semibold text-right tabular-nums pt-1 border-t mt-1">
              Total ₹{data.total.toLocaleString('en-IN')}
            </p>
          ) : null}
        </div>
      ) : !hintsOnly && data && !loading ? (
        <p className="text-[11px] text-muted-foreground">No cash movements for this line in the period.</p>
      ) : null}
      {canDownload ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-full text-xs gap-1.5"
          disabled={downloading || loading}
          onClick={onDownload}
        >
          <Download className="h-3.5 w-3.5" />
          {downloading ? 'Preparing CSV…' : 'Download calculation CSV'}
        </Button>
      ) : null}
    </div>
  );
}

export function CashFlowBreakdownHover({
  lineId,
  label,
  consolidated,
  periodMode,
  periodKey,
  fyStartYear,
  hospitalId,
  className,
  children,
}: CashFlowBreakdownHoverProps) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hints = cfBreakdownCategoryHints(lineId);
  const hintsOnly = !periodKey;
  const canFetch =
    !hintsOnly &&
    isCfBreakdownLineId(lineId) &&
    (periodMode === 'MTD' || !!fyStartYear);

  const { data, isLoading } = useQuery({
    queryKey: [
      'cash-flow-breakdown',
      lineId,
      periodMode,
      periodKey,
      fyStartYear,
      hospitalId,
      consolidated,
    ],
    queryFn: () =>
      businessReportsApi.cashFlowBreakdown({
        lineId,
        periodMode,
        month: periodMode === 'MTD' ? periodKey : undefined,
        fy: periodMode === 'YTD' ? fyStartYear : undefined,
        hospitalId,
      }),
    enabled: open && canFetch,
    staleTime: 60_000,
  });

  const handleDownload = async () => {
    if (!canFetch) return;
    setDownloading(true);
    try {
      await downloadCashFlowBreakdownCsv({
        lineId,
        periodMode,
        periodKey,
        fyStartYear,
        hospitalId,
      });
      toast.success('Cash flow calculation downloaded');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : null) ||
        'Failed to download CSV';
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  if (!hints) {
    return <>{children ?? <span className={className}>{label}</span>}</>;
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  const handleEnter = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const handleCellClick = (e: React.MouseEvent) => {
    if (!canFetch || !children) return;
    e.preventDefault();
    e.stopPropagation();
    void handleDownload();
  };

  return (
    <Popover open={open} modal={false}>
      <PopoverAnchor asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1',
            children ? 'cursor-pointer w-full justify-end' : 'cursor-help',
            className
          )}
          onMouseEnter={handleEnter}
          onMouseLeave={scheduleClose}
          onFocus={handleEnter}
          onBlur={scheduleClose}
          onClick={children ? handleCellClick : undefined}
          title={canFetch && children ? 'Click to download calculation CSV' : undefined}
          tabIndex={children ? -1 : 0}
          role={children ? undefined : 'button'}
          aria-label={children ? undefined : `${label} — cash flow breakdown`}
        >
          {children ?? (
            <>
              <span className="border-b border-dotted border-slate-400">{label}</span>
              <Info className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            </>
          )}
        </span>
      </PopoverAnchor>
      <PopoverContent
        className="w-[min(24rem,calc(100vw-2rem))] p-3"
        align={children ? 'end' : 'start'}
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        onMouseEnter={handleEnter}
        onMouseLeave={scheduleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-xs font-semibold text-slate-900 mb-2">{label}</p>
        <BreakdownPanel
          data={data}
          hints={hints}
          loading={isLoading}
          hintsOnly={hintsOnly}
          onDownload={() => void handleDownload()}
          downloading={downloading}
          canDownload={canFetch}
        />
      </PopoverContent>
    </Popover>
  );
}
